/**
 * ReferralSourcePanel — owner-only marketing attribution widget.
 *
 * Hits /api/coach/assessment-requests/referral-stats and renders the
 * breakdown of how new leads found the gym. Shows per-source counts +
 * the free-text "who referred you?" answers people typed.
 *
 * Lives in the Coach Dashboard, between Demo Workspace and Search bar.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { TrendingUp, ChevronDown, ChevronUp } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SOURCE_LABELS = {
    google: "Google Search",
    referral: "Referral",
    other: "Other",
};

export default function ReferralSourcePanel() {
    const [stats, setStats] = useState(null);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        axios
            .get(`${API}/coach/assessment-requests/referral-stats`, { withCredentials: true })
            .then((r) => setStats(r.data))
            .catch(() => setStats(null))
            .finally(() => setLoading(false));
    }, []);

    if (loading || !stats) return null;
    const total = stats.total_with_source || 0;
    const sources = Object.entries(stats.by_source || {});

    return (
        <div
            data-testid="referral-source-panel"
            className="border border-zinc-900 bg-black/40 rounded-lg"
        >
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                data-testid="referral-source-panel-toggle"
                className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-zinc-900/40 transition-colors"
            >
                <div className="flex items-center gap-3 min-w-0">
                    <TrendingUp className="w-4 h-4 text-brand-red flex-shrink-0" />
                    <div className="min-w-0">
                        <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-300">
                            How leads find us
                        </div>
                        <div className="text-sm text-white truncate">
                            {total === 0
                                ? "No source data yet — new submissions will populate here."
                                : sources
                                    .sort((a, b) => b[1] - a[1])
                                    .map(([k, v]) => `${SOURCE_LABELS[k] || k}: ${v}`)
                                    .join(" · ")}
                        </div>
                    </div>
                </div>
                {open ? (
                    <ChevronUp className="w-4 h-4 text-zinc-300 flex-shrink-0" />
                ) : (
                    <ChevronDown className="w-4 h-4 text-zinc-300 flex-shrink-0" />
                )}
            </button>

            {open && (
                <div className="border-t border-zinc-900 px-4 py-4 space-y-4">
                    {/* Per-source breakdown with proportional bars */}
                    <div className="space-y-2">
                        {sources.length === 0 && (
                            <p className="text-xs text-zinc-300">
                                Once parents fill out the "How did you find us?" question on the Book
                                Assessment form, you'll see the channel split here.
                            </p>
                        )}
                        {sources
                            .sort((a, b) => b[1] - a[1])
                            .map(([k, v]) => {
                                const pct = total > 0 ? Math.round((v / total) * 100) : 0;
                                return (
                                    <div
                                        key={k}
                                        data-testid={`referral-source-row-${k}`}
                                        className="flex items-center gap-3"
                                    >
                                        <div className="w-28 text-xs uppercase tracking-[0.15em] text-zinc-300 flex-shrink-0">
                                            {SOURCE_LABELS[k] || k}
                                        </div>
                                        <div className="flex-1 bg-zinc-900 h-2 rounded overflow-hidden">
                                            <div
                                                className="h-full bg-brand-red"
                                                style={{ width: `${pct}%` }}
                                            />
                                        </div>
                                        <div className="text-xs text-white tabular-nums w-20 text-right flex-shrink-0">
                                            {v} <span className="text-zinc-400">({pct}%)</span>
                                        </div>
                                    </div>
                                );
                            })}
                        {stats.unanswered > 0 && (
                            <p className="text-[10px] text-zinc-500 mt-2">
                                {stats.unanswered} legacy submission(s) didn't have this question
                                yet — they're excluded.
                            </p>
                        )}
                    </div>

                    {/* Referral examples — show who referred recent signups */}
                    {(stats.referral_examples || []).length > 0 && (
                        <div className="border-t border-zinc-900 pt-3">
                            <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-300 mb-2">
                                Recent referrals · who they named
                            </div>
                            <div className="space-y-1.5 max-h-48 overflow-y-auto">
                                {stats.referral_examples.map((ex, i) => (
                                    <div
                                        key={i}
                                        className="flex items-baseline gap-2 text-xs"
                                        data-testid={`referral-example-${i}`}
                                    >
                                        <span className="text-white font-medium">
                                            {ex.parent_name || "Unknown"}
                                        </span>
                                        <span className="text-zinc-400">→</span>
                                        <span className="text-zinc-200 truncate">{ex.details}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
